/**
 * A postal address as ONE field.
 *
 * Street, city, state and ZIP are four inputs and one answer. Modelling them as four separate
 * fields is what the form does today, and it costs something at every layer: four entries in the
 * field list to move and re-label together, four conditions to keep in step when an address is
 * revealed conditionally, four rules to copy an address from one place to another, and — the one
 * that actually bites — four separate "or explain" prompts when an address is soft-required,
 * because that mechanism is per FIELD. As a single field with a single path, an address gets one
 * label, one condition, one copy rule and one explanation.
 *
 * The value is an object at that path: `{ street, street2, city, state, zip }`. Dotted paths
 * still reach inside it, so a feed column can target `custom.billing_address.city` directly and a
 * mirror rule can copy a whole address in one move.
 *
 * US-only, deliberately: the firm is a California law firm and every address it bills to is
 * domestic. A country field and per-country formats would be scaffolding for a case that does not
 * arise, and the state list is what makes a future feed match possible at all.
 */

/** The parts, in the order they are entered and printed. */
export const ADDRESS_PARTS = ['street', 'street2', 'city', 'state', 'zip'];

/** Everything except the second street line, which is genuinely optional in a way the rest is not. */
export const REQUIRED_ADDRESS_PARTS = ['street', 'city', 'state', 'zip'];

export const ADDRESS_LABELS = {
  street: 'Street address',
  street2: 'Suite, floor, etc.',
  city: 'City',
  state: 'State',
  zip: 'ZIP',
};

/**
 * Two-letter USPS codes. Stored rather than the full name, because that is what an address label
 * carries, what Aderant holds, and what a future feed would have to match on.
 */
export const US_STATES = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'],
  ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
  ['DC', 'District of Columbia'], ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'],
  ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'],
  ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'],
  ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'],
  ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'],
  ['NV', 'Nevada'], ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'],
  ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'],
  ['OK', 'Oklahoma'], ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['PR', 'Puerto Rico'],
  ['RI', 'Rhode Island'], ['SC', 'South Carolina'], ['SD', 'South Dakota'], ['TN', 'Tennessee'],
  ['TX', 'Texas'], ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'], ['VI', 'Virgin Islands'],
  ['WA', 'Washington'], ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
];

const STATE_CODES = new Set(US_STATES.map(([code]) => code));

/** Tolerates whatever shape a value arrives in, including a plain string from an older field. */
export function toAddress(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    // A single string is treated as the street line rather than discarded, so retyping a field as
    // an address keeps what was already entered instead of silently emptying it.
    return { street: typeof value === 'string' ? value : '', street2: '', city: '', state: '', zip: '' };
  }
  const address = {};
  for (const part of ADDRESS_PARTS) address[part] = String(value[part] ?? '').trim();
  // Stored upper-case so the value is comparable: a future feed matches on the USPS code, and
  // 'ca' and 'CA' being different strings is the kind of thing that only shows up later.
  address.state = address.state.toUpperCase();
  return address;
}

export function isAddressEmpty(value) {
  const address = toAddress(value);
  return ADDRESS_PARTS.every((part) => !address[part]);
}

/** Lower-cased for a sentence, except ZIP, which is an initialism and looks wrong as "zip". */
function partNoun(part) {
  return part === 'zip' ? 'ZIP' : ADDRESS_LABELS[part].toLowerCase();
}

/** 12345 or 12345-6789, the two forms USPS actually uses. */
export function isValidZip(zip) {
  return /^\d{5}(-\d{4})?$/.test(String(zip || '').trim());
}

/**
 * What is wrong with an address, or null.
 *
 * An address that is entirely empty is NOT a problem here — whether it may be empty is the
 * field's requirement level, exactly as for any other field. What this catches is the state
 * between the two: a half-filled address passes a plain "is it blank" check (something is in
 * there) while being useless to post to. That is the same trap a repeating group's empty rows and
 * an origination split's placeholder rows both fell into.
 *
 * @returns {{message: string, missing: string[]} | null}
 */
export function addressProblem(value, { label = 'Address' } = {}) {
  const address = toAddress(value);
  if (isAddressEmpty(address)) return null;

  const missing = REQUIRED_ADDRESS_PARTS.filter((part) => !address[part]);
  if (missing.length) {
    return {
      missing,
      message: `${label} is missing ${missing.map(partNoun).join(', ')}.`,
    };
  }
  if (!isValidZip(address.zip)) {
    return { missing: ['zip'], message: `${label}: "${address.zip}" is not a valid ZIP code.` };
  }
  if (!STATE_CODES.has(address.state.toUpperCase())) {
    return { missing: ['state'], message: `${label}: "${address.state}" is not a US state.` };
  }
  return null;
}

/**
 * One line, for a table cell or an email. Uses the parts that are present rather than leaving the
 * gaps of an address template, so a partial address still reads as an address.
 */
export function formatAddressOneLine(value) {
  const address = toAddress(value);
  const street = [address.street, address.street2].filter(Boolean).join(', ');
  const region = [address.city, [address.state, address.zip].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  return [street, region].filter(Boolean).join(', ');
}

/** How an address is printed on the matter record: as it would be addressed on an envelope. */
export function formatAddressLines(value) {
  const address = toAddress(value);
  const lines = [address.street, address.street2].filter(Boolean);
  const region = [address.city, [address.state, address.zip].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  if (region) lines.push(region);
  return lines;
}
